import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/apiConfig';
import './PlayerDetail.scss';

// Helper function to convert numbers to Roman numerals
function romanNumeral(num) {
    const romanMap = [
        { value: 10, numeral: 'X' },
        { value: 9, numeral: 'IX' },
        { value: 5, numeral: 'V' },
        { value: 4, numeral: 'IV' },
        { value: 1, numeral: 'I' }
    ];

    let result = '';
    for (const { value, numeral } of romanMap) {
        while (num >= value) {
            result += numeral;
            num -= value;
        }
    }
    return result;
}

// Helper function to get Minecraft item image URL
function getItemImageUrl(itemId) {
    // Use local item images from public/items folder
    // Format: minecraft:item_name -> item_name.png
    const cleanId = itemId.replace('minecraft:', '');
    return `/items/${cleanId}.png`;
}

const PlayerDetail = () => {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const [playerData, setPlayerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const showOnMap = () => {
        if (playerData && playerData.coordinates) {
            const { x, z } = playerData.coordinates;
            // Navigate to map with player coordinates as center and zoom level 4
            // We'll pass coordinates as URL params that the map can read
            navigate(`/map?center=${x},${z}&zoom=4`);
        }
    };

    // Helper function to render visual health bar with hearts (each heart = 2 health points)
    const renderHealthBar = (current, max = 20) => {
        const totalHearts = max / 2; // 10 hearts for 20 health points
        const hearts = [];
        for (let i = 0; i < totalHearts; i++) {
            const heartValue = (i + 1) * 2; // Each heart represents 2 points
            let heartClass = 'empty';
            if (current >= heartValue) {
                heartClass = 'filled';
            } else if (current >= heartValue - 1) {
                heartClass = 'half';
            }
            hearts.push(
                <span
                    key={i}
                    className={`health-icon ${heartClass}`}
                >
                    ❤️
                </span>
            );
        }
        return hearts;
    };

    // Helper function to render visual food bar with meat icons (each icon = 2 food points)
    const renderFoodBar = (current, max = 20) => {
        const totalFood = max / 2; // 10 food icons for 20 food points
        const food = [];
        for (let i = 0; i < totalFood; i++) {
            const foodValue = (i + 1) * 2; // Each food icon represents 2 points
            let foodClass = 'empty';
            if (current >= foodValue) {
                foodClass = 'filled';
            } else if (current >= foodValue - 1) {
                foodClass = 'half';
            }
            food.push(
                <span
                    key={i}
                    className={`food-icon ${foodClass}`}
                >
                    🍖
                </span>
            );
        }
        return food;
    };

    const fetchPlayerData = useCallback(async () => {
        try {
            setError(null);
            setLoading(true);
            const response = await apiRequest(`/player/${encodeURIComponent(playerId)}`);
            const data = await response.json();
            setPlayerData(data);
            setLoading(false);
        } catch (err) {
            if (err.message.includes('404')) {
                setError('Player not found or not online');
            } else {
                setError(err.message);
            }
            setLoading(false);
        }
    }, [playerId]);

    useEffect(() => {
        fetchPlayerData();
        const interval = setInterval(fetchPlayerData, 300000); // Refresh every 5 minutes
        return () => clearInterval(interval);
    }, [fetchPlayerData]);

    if (loading) {
        return (
            <div className="player-detail">
                <Link to="/" className="back-button">← Back to Home</Link>
                <h1>Player Details</h1>
                <p>Loading player data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="player-detail">
                <Link to="/" className="back-button">← Back to Home</Link>
                <h1>Player Details</h1>
                <p className="error">Error: {error}</p>
                <button onClick={fetchPlayerData}>Retry</button>
            </div>
        );
    }

    return (
        <div className="player-detail">
            <Link to="/" className="back-button">← Back to Home</Link>
            <h1>Player Details</h1>

            {playerData && (
                <div>
                    <div className="player-header">
                        <img
                            src={`https://mc-heads.net/body/${playerData.name}`}
                            alt={`${playerData.name}'s skin`}
                            className="player-skin"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div className="player-info">
                            <h2>{playerData.name}</h2>
                            <div className="status status-item">
                                <strong>Status: </strong>
                                <span className={`status-indicator ${playerData.online ? 'online' : 'offline'}`}>
                                    {playerData.online ? 'Online' : 'Offline'}
                                </span>
                            </div>
                            <div className="gamemode status-item">
                                <strong>Game Mode: </strong>
                                {playerData.gameMode && (playerData.gameMode)}
                            </div>
                            <div className="location">
                                <strong>Location: </strong>
                                {playerData.dimension && playerData.dimension === 'overworld' ? 'Overworld' : playerData.dimension === 'the_nether' ? 'The Nether' : playerData.dimension === 'the_end' ? 'The End' : playerData.dimension}
                                <i> ({Math.round(playerData.coordinates.x)}, {Math.round(playerData.coordinates.y)}, {Math.round(playerData.coordinates.z)})</i>
                            </div>
                        </div>
                        <button
                            className="show-on-map-button"
                            onClick={showOnMap}
                            title="View player location on map"
                        >
                            <img
                                src={'/items/filled_map.png'}
                                alt="Map"
                                className="item-image"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />Show on Map
                        </button>
                    </div>

                    {playerData.online && (
                        <div className="player-details">
                            {/* Basic Stats */}
                            <div className="detail-section">
                                <h3>Player Stats</h3>
                                <div className="stats-grid">
                                    {playerData.health !== null && (
                                        <div className="stat-item">
                                            <strong>Health:</strong> {playerData.health}/20
                                            <div className="visual-bar">
                                                {renderHealthBar(playerData.health)}
                                            </div>
                                        </div>
                                    )}
                                    {playerData.food !== null && (
                                        <div className="stat-item">
                                            <strong>Food:</strong> {playerData.food}/20
                                            <div className="visual-bar">
                                                {renderFoodBar(playerData.food)}
                                            </div>
                                        </div>
                                    )}
                                    {(playerData.experienceLevel !== null || playerData.totalExperience !== null) && (
                                        <div className="stat-item experience-container">
                                            <div className="experience-header">
                                                <strong>Experience:</strong>
                                                {playerData.experienceLevel !== null && (
                                                    <span className="experience-level"> Level {playerData.experienceLevel}</span>
                                                )}
                                            </div>
                                            {playerData.experienceProgress !== null && (
                                                <div className="xp-bar-container">
                                                    <div className="xp-bar">
                                                        <div
                                                            className="xp-bar-fill"
                                                            style={{ width: `${playerData.experienceProgress * 100}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            )}
                                            {playerData.totalExperience !== null && (
                                                <div className="experience-total">
                                                    <small>Total XP: {playerData.totalExperience.toLocaleString()}</small>
                                                </div>
                                            )}
                                        </div>
                                    )}


                                </div>
                            </div>

                            {/* Inventory */}
                            {playerData.inventory && playerData.inventory.length > 0 && (
                                <div className="detail-section">
                                    <h3>Inventory ({playerData.inventory.length} items)</h3>
                                    <div className="inventory-grid">
                                        {playerData.inventory
                                            .sort((a, b) => a.slot - b.slot)
                                            .map((item, index) => (
                                                <div key={index} className="inventory-item">
                                                    <img
                                                        src={getItemImageUrl(item.id)}
                                                        alt={item.id}
                                                        className="item-image"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="item-icon-fallback" style={{ display: 'none' }}>📦</div>
                                                    {item.count > 1 && <span className="item-count-badge">{item.count}</span>}
                                                    <div className="item-tooltip">
                                                        <div className="tooltip-title">{item.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                                                        <div className="tooltip-info">Count: {item.count}</div>
                                                        {item.slot !== undefined && <div className="tooltip-info">Slot: {item.slot}</div>}
                                                        {item.damage !== undefined && item.damage > 0 && <div className="tooltip-info">Damage: {item.damage}</div>}
                                                        {item.enchantments && item.enchantments.length > 0 && (
                                                            <div className="tooltip-enchantments">
                                                                <div className="tooltip-enchant-title">Enchantments:</div>
                                                                {item.enchantments.map((ench, i) => (
                                                                    <div key={i} className="tooltip-enchant">
                                                                        {ench.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} {ench.level > 1 ? romanNumeral(ench.level) : 'I'}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Ender Chest */}
                            {playerData.enderItems && playerData.enderItems.length > 0 && (
                                <div className="detail-section">
                                    <h3><img
                                        src={getItemImageUrl('ender_chest')}
                                        alt="Ender Chest"
                                        className="item-image"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    /> Ender Chest ({playerData.enderItems.length} items)</h3>
                                    <div className="inventory-grid">
                                        {playerData.enderItems
                                            .sort((a, b) => a.slot - b.slot)
                                            .map((item, index) => (
                                                <div key={index} className="inventory-item ender-item">
                                                    <img
                                                        src={getItemImageUrl(item.id)}
                                                        alt={item.id}
                                                        className="item-image"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="item-icon-fallback" style={{ display: 'none' }}>📦</div>
                                                    {item.count > 1 && <span className="item-count-badge">{item.count}</span>}
                                                    <div className="item-tooltip">
                                                        <div className="tooltip-title">{item.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                                                        <div className="tooltip-info">Count: {item.count}</div>
                                                        {item.slot !== undefined && <div className="tooltip-info">Slot: {item.slot}</div>}
                                                        {item.damage !== undefined && item.damage > 0 && <div className="tooltip-info">Damage: {item.damage}</div>}
                                                        {item.enchantments && item.enchantments.length > 0 && (
                                                            <div className="tooltip-enchantments">
                                                                <div className="tooltip-enchant-title">Enchantments:</div>
                                                                {item.enchantments.map((ench, i) => (
                                                                    <div key={i} className="tooltip-enchant">
                                                                        {ench.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} {ench.level > 1 ? romanNumeral(ench.level) : 'I'}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Raw Data for Debug */}
                            <div className="detail-section">
                                <h3>All Available Data</h3>
                                <details>
                                    <summary>Show Raw Data</summary>
                                    <pre className="raw-data">
                                        {JSON.stringify(playerData, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        </div>
                    )}

                    {!playerData.online && (
                        <p className="offline-message">This player is currently offline. Player data is only available for online players.</p>
                    )}

                    <div className="last-updated">
                        Last updated: {new Date(playerData.updatedAt).toLocaleString()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlayerDetail;